// store/modules/statistics.js
import axios from '@/axios';
import { normalizeForBackend } from '@/utils/normalizeData';

const stores = [
  { key: 'spotify', name: 'Spotify', color: '#64D561' },
  { key: 'apple_music', name: 'Apple', color: '#FB223C' },
  { key: 'youtube', name: 'YouTube', color: '#FF0000' },
  { key: 'vkontakte', name: 'VKontakte', color: '#0078FF' },
  { key: 'boom', name: 'BOOM', color: '#FE2958' },
  { key: 'deezer', name: 'Deezer', color: '#EF9F1A' },
  { key: 'itunes', name: 'iTunes', color: '#E049D4' },
  { key: 'saavn', name: 'Saavn', color: '#30B55C' },
];

const initialState = () => ({
  statistics: [],        // [{ uuid, streams: [ { date, spotify, ... } ] }]
  commonStatistics: [],  // [ { date, spotify, ... } ]
  stores,
});

const statistics = {
  namespaced: false, // можно включить, если хочешь
  state: () => initialState(),

  actions: {
    // POST /track-statistics  (по конкретному релизу)
    fetchReleaseStatistics({ commit }, payload) {
      const releaseUuid = payload.release_uuid || payload.releaseUuid;
      return new Promise((resolve, reject) => {
        axios.post('/track-statistics', normalizeForBackend(payload))
          .then((response) => {
            const release = { uuid: releaseUuid, streams: response.data };
            commit('updateReleaseStatistics', release);
            resolve(response.data);
          })
          .catch(reject);
      });
    },

    // GET /release-statistics (массив релизов с массивами дней)
    fetchCommonStatistics({ commit }, params) {
      return new Promise((resolve, reject) => {
        axios.get('/release-statistics', { params })
          .then((response) => {
            const commonStatistics = response.data.data.filter((r) => r.streams?.length);
            commit('setStatistics', commonStatistics);
            resolve(response);
          })
          .catch(reject);
      });
    },

    // GET /common-statistics (агрегированные дни по всем релизам)
    fetchCommonStreams({ commit }, params) {
      return new Promise((resolve, reject) => {
        axios.get('/common-statistics', { params })
          .then((response) => {
            const commonStatistics = response.data;
            commit('setСommonStatistics', commonStatistics);
            resolve(response.data);
          })
          .catch(reject);
      });
    },
  },

  mutations: {
    resetState(state) { Object.assign(state, initialState()); },
    updateReleaseStatistics(state, release) {
      const idx = state.statistics.findIndex((r) => r.uuid === release.uuid);
      if (idx >= 0) state.statistics[idx] = release;
      else state.statistics.push(release);
    },
    setStatistics(state, statistics) { state.statistics = statistics; },
    setСommonStatistics(state, statistics) { state.commonStatistics = statistics; },
  },

  getters: {
    statistics: (state) => state.statistics,
    releaseStatistics: (state) => (uuid) => state.statistics.find((s) => s.uuid === uuid),
    commonStatistics: (state) => state.commonStatistics,

    streamsByDays: (state, getters) => {
      const byDate = [];
      state.statistics.forEach((release) => {
        release.streams.forEach((day) => {
          const date = day.date;
          const dayStreams = getters.dayStreams(day);
          const i = byDate.findIndex((x) => x.date === date);
          if (i === -1) byDate.push({ date, streams: dayStreams });
          else byDate[i].streams += dayStreams;
        });
      });
      byDate.sort((a, b) => new Date(a.date) - new Date(b.date));
      return byDate;
    },

    releaseStreamsByDays: (state, getters) => (releaseUuid) => {
      const release = getters.releaseStatistics(releaseUuid);
      if (!release?.streams) return [];
      const byDate = [];
      release.streams.forEach((day) => {
        const date = day.date;
        const dayStreams = getters.dayStreams(day);
        const i = byDate.findIndex((x) => x.date === date);
        if (i === -1) byDate.push({ date, streams: dayStreams });
        else byDate[i].streams += dayStreams;
      });
      byDate.sort((a, b) => new Date(a.date) - new Date(b.date));
      return byDate;
    },

    commonTotalStreams: (state, getters) =>
      state.commonStatistics.reduce((total, day) => total + getters.dayStreams(day), 0),

    totalStreams: (state, getters) =>
      state.statistics.reduce((t, r) => t + getters.releaseTotalStreams(r.uuid), 0),

    releaseTotalStreams: (state, getters) => (releaseUuid) => {
      const release = getters.releaseStatistics(releaseUuid);
      if (!release?.streams) return 0;
      return release.streams.reduce((t, day) => t + getters.dayStreams(day), 0);
    },

    dayStreams: (state, getters) => (day) =>
      getters.storeKeys.reduce((t, key) => t + Number(day[key] || 0), 0),

    streamsByStores: (state, getters) => getters.storeKeys.map((key) => getters.storeStreams(key)),

    releaseStreamsByStores: (state, getters) => (releaseUuid) =>
      getters.storeKeys.map((key) => getters.releaseStoreStreams(releaseUuid, key)),

    releaseStoreStreams: (state, getters) => (releaseUuid, key) => {
      const release = getters.releaseStatistics(releaseUuid);
      if (!release?.streams) return 0;
      return release.streams.reduce((t, day) => t + Number(day[key] || 0), 0);
    },

    storeStreams: (state, getters) => (key) => {
      return state.statistics.reduce((total, release) => {
        return total + getters.releaseStoreStreams(release.uuid, key);
      }, 0);
    },
    

    storeStreamsPercentage: (state, getters) => (key) => {
      const storeStreams = getters.storeStreams(key);
      const totalStreams = getters.totalStreams;
      if (!storeStreams || !totalStreams) return 0;
      return Math.round((storeStreams / totalStreams) * 100);
    },

    releaseStoreStreamsPercentage: (state, getters) => (uuid, key) => {
      const val = getters.releaseStoreStreams(uuid, key);
      const total = getters.releaseTotalStreams(uuid);
      if (!val || !total) return 0;
      return Math.round((val / total) * 100);
    },

    storeKeys: (state) => state.stores.map((s) => s.key),
    storeNames: (state) => state.stores.map((s) => s.name),
    storeColors: (state) => state.stores.map((s) => s.color),
  },
};

export default statistics;
